import { Button, Collapse, Dialog, DialogContent, DialogTitle, FormControl, FormControlLabel, Grid, InputAdornment, InputLabel, MenuItem, Select, SelectChangeEvent, Slide, Stack, Switch, TextField, Typography } from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { useSnackbar } from "notistack";
import React, { ReactElement } from "react";
import { ApiError } from "../../Api/ApiError";
import { EditEquipmentData, getEquipmentCategoryList, getEquipmentGroupList, RegEquipmentData } from "../../Api/Equipment";
import { EquipmentCategory } from "../../Objects/EquipmentCategory";
import { EquipmentChargeType, EquipmentChargeTypeString, EquipmentData } from "../../Objects/EquipmentData";
import { EquipmentGroup } from "../../Objects/EquipmentGroup";
import { StoreObjectStore } from "../../ObjectStore/StoreObjectStore";


const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: ReactElement<any, any>;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />
})

export interface EquipmentEditDialogProps {
    open: boolean
    onClose: (isEdit: boolean) => void
    equipmentData?: EquipmentData
    str_id: number
}

const EquipmentEditDialog: React.FC<EquipmentEditDialogProps> = ({
    open,
    onClose,
    equipmentData,
    str_id,
}) => {
    let { enqueueSnackbar } = useSnackbar()
    const { storeObjectState } = React.useContext(StoreObjectStore)

    const [dialogOpen, setDialogOpen] = React.useState<boolean>(false)
    const [disableSubmit, setDisableSubmit] = React.useState<boolean>(true)

    const [editEquipment, setEditEquipment] = React.useState<EquipmentData>({})
    const [equipmentCategoryList, setEquipmentCategoryList] = React.useState<EquipmentCategory[]>([])
    const [equipmentGroupList, setEquipmentGroupList] = React.useState<EquipmentGroup[]>([])

    const abortController = React.useMemo(() => new AbortController(), [])

    React.useEffect(() => {
        return () => {
            abortController.abort()
        }
    }, [abortController])

    const doSave = React.useCallback((equData: EquipmentData) => {
        if (equData.equ_id) {
            // 編集
            EditEquipmentData({
                equipmentData: equData
            }, {signal: abortController.signal}).then(() => {
                enqueueSnackbar("保存しました", { variant: "success" })
                onClose(true)
            }).catch((e: any) => {
                if( e.__CANCEL__ ) {
                    console.info("canceled")
                } else if (e instanceof ApiError) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else if (e instanceof Error) {
                    enqueueSnackbar(e.message, { variant: "error" })
                }else {
                    enqueueSnackbar("通信に失敗しました", { variant: "error" })
                }
            })
        } else {
            // 新規
            RegEquipmentData({
                equipmentData: {
                    ...equData,
                    str_id: str_id
                }
            }, {signal: abortController.signal}).then(() => {
                enqueueSnackbar("保存しました", { variant: "success" })
                onClose(true)
            }).catch((e: any) => {
                if( e.__CANCEL__ ) {
                    console.info("canceled")
                } else if (e instanceof ApiError) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else if (e instanceof Error) {
                    enqueueSnackbar(e.message, { variant: "error" })
                }else {
                    enqueueSnackbar("通信に失敗しました", { variant: "error" })
                }
            })
        }
    },[abortController, enqueueSnackbar, onClose, str_id])

    React.useEffect(() => {
        const controller = new AbortController()
        getEquipmentCategoryList({signal: controller.signal}).then(result => {
            setEquipmentCategoryList(result.list)
        }).catch((e: any) => {
            if( e.__CANCEL__ ) {
                console.info("canceled")
            } else if (e instanceof ApiError) {
                enqueueSnackbar(e.message, { variant: "error" })
            } else if (e instanceof Error) {
                enqueueSnackbar(e.message, { variant: "error" })
            }else {
                enqueueSnackbar("通信に失敗しました", { variant: "error" })
            }
        })
        return () => {
            controller.abort()
        }
    }, [enqueueSnackbar])

    React.useEffect(() => {
        const controller = new AbortController()
        getEquipmentGroupList({
            str_id: str_id
        }, {signal: controller.signal}).then(result => {
            setEquipmentGroupList(result.list)
        }).catch((e: any) => {
            if( e.__CANCEL__ ) {
                console.info("canceled")
            } else if (e instanceof ApiError) {
                enqueueSnackbar(e.message, { variant: "error" })
            } else if (e instanceof Error) {
                enqueueSnackbar(e.message, { variant: "error" })
            }else {
                enqueueSnackbar("通信に失敗しました", { variant: "error" })
            }
        })
        return () => {
            controller.abort()
        }
    }, [str_id, enqueueSnackbar])


    React.useEffect(() => {
        if (open) {
            setDialogOpen(true)
        } else {
            setDialogOpen(false)
        }
    }, [open])

    React.useEffect(() => {
        if (equipmentData) {
            setEditEquipment({
                ...equipmentData,
                available_studio_ids: equipmentData.available_studio_ids?.concat()
            })
        } else {
            setEditEquipment({})
        }
    }, [equipmentData])

    React.useEffect(() => {
        if (editEquipment.equcate_id && editEquipment.equ_name) {
            setDisableSubmit(false)
        } else {
            setDisableSubmit(true)
        }
    }, [editEquipment])


    return (
        <Dialog
            open={dialogOpen}
            TransitionComponent={Transition}
            onClose={() => {
                onClose(false)
            }}
            fullWidth
        >
            <DialogTitle>{equipmentData ? "機材編集" : "機材登録"}</DialogTitle>
            <DialogContent
                sx={{
                    overflowY: "visible"
                }}
            >
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12}>
                        <FormControl
                            sx={{
                                width: "100%"
                            }}
                            size="small"
                        >
                            <InputLabel id="equipmentCategory">機材カテゴリ</InputLabel>
                            <Select
                                labelId="equipmentCategory"
                                value={editEquipment?.equcate_id ? editEquipment.equcate_id : 0}
                                onChange={(e: SelectChangeEvent<number>) => {
                                    let val = typeof e.target.value === "string" ? parseInt(e.target.value) : e.target.value
                                    let cp: EquipmentData = {
                                        ...editEquipment,
                                        equcate_id: val === 0 ? undefined : val
                                    }
                                    setEditEquipment(cp)
                                }}
                                label="機材カテゴリ"
                                fullWidth
                                size="small"
                            >
                                <MenuItem value={0} disabled />
                                {equipmentCategoryList.map(value => (
                                    <MenuItem value={value.equcate_id} key={value.equcate_id}>{value.equcate_name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label="機材名"
                            value={editEquipment.equ_name ? editEquipment.equ_name : ""}
                            onChange={(e) => {
                                setEditEquipment({
                                    ...editEquipment,
                                    equ_name: e.target.value
                                })
                            }}
                            fullWidth
                            size="small"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label="メーカー名"
                            value={editEquipment.equ_maker ? editEquipment.equ_maker : ""}
                            onChange={(e) => {
                                setEditEquipment({
                                    ...editEquipment,
                                    equ_maker: e.target.value
                                })
                            }}
                            fullWidth
                            size="small"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label="型番"
                            value={editEquipment.equ_model ? editEquipment.equ_model : ""}
                            onChange={(e) => {
                                setEditEquipment({
                                    ...editEquipment,
                                    equ_model: e.target.value
                                })
                            }}
                            fullWidth
                            size="small"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="短縮文字（英数字4文字まで）"
                            // helperText="英数字4文字まで"
                            value={editEquipment.equ_shortened ? editEquipment.equ_shortened : ""}
                            onChange={(e) => {
                                setEditEquipment({
                                    ...editEquipment,
                                    equ_shortened: e.target.value
                                })
                            }}
                            inputProps={{
                                maxLength: 4,
                                pattern: "^[a-zA-Z0-9_]+$",
                            }}
                            fullWidth
                            size="small"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormControlLabel
                            control={<Switch
                                color="secondary"
                                checked={editEquipment.rental_flug ? editEquipment.rental_flug : false}
                                onChange={e => {
                                    setEditEquipment({
                                        ...editEquipment,
                                        rental_flug: e.target.checked
                                    })
                                }}
                            />}
                            label="レンタル機材として登録"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Collapse
                            in={editEquipment.rental_flug}
                        >
                            <Grid container>
                                <Grid item xs={4}>
                                    <FormControl
                                        sx={{
                                            width: "100%"
                                        }}
                                        size="small"
                                    >
                                        <InputLabel id="equipmentChargeType">レンタル料金</InputLabel>
                                        <Select
                                            labelId="equipmentChargeType"
                                            value={editEquipment?.equ_charge_type ? editEquipment.equ_charge_type : EquipmentChargeType.null}
                                            onChange={(e: SelectChangeEvent<EquipmentChargeType>) => {
                                                console.log(e.target.value)
                                                setEditEquipment({
                                                    ...editEquipment,
                                                    equ_charge_type: e.target.value as EquipmentChargeType
                                                })
                                            }}
                                            label="レンタル料金"
                                            fullWidth
                                            size="small"
                                        >
                                            <MenuItem value={EquipmentChargeType.null}>{EquipmentChargeTypeString.null}</MenuItem>
                                            <MenuItem value={EquipmentChargeType.hour}>{EquipmentChargeTypeString.hour}</MenuItem>
                                            <MenuItem value={EquipmentChargeType.time}>{EquipmentChargeTypeString.time}</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                {(editEquipment.equ_charge_type === EquipmentChargeType.hour || editEquipment.equ_charge_type === EquipmentChargeType.time) &&
                                    <Grid item xs={8}>
                                        <TextField
                                            value={editEquipment.equ_charge ? editEquipment.equ_charge : ""}
                                            onChange={(e) => {
                                                setEditEquipment({
                                                    ...editEquipment,
                                                    equ_charge: isNaN(Number(e.target.value)) ? undefined : Number(e.target.value)
                                                })
                                            }}
                                            inputMode="numeric"
                                            type="number"
                                            inputProps={{
                                                style: {
                                                    textAlign: "right"
                                                }
                                            }}
                                            InputProps={{
                                                sx: {
                                                    textAlign: "right"
                                                },
                                                endAdornment: <InputAdornment position="end">円</InputAdornment>
                                            }}
                                            size="small"
                                            fullWidth
                                        />
                                    </Grid>
                                }
                            </Grid>
                        </Collapse>
                    </Grid>

                    <Grid item xs={12}>
                        <FormControl
                            sx={{
                                width: "100%"
                            }}
                            size="small"
                        >
                            <InputLabel id="equipmentGroup">機材グループ</InputLabel>
                            <Select
                                labelId="equipmentGroup"
                                value={editEquipment?.equgrp_id ? editEquipment.equgrp_id : 0}
                                onChange={(e: SelectChangeEvent<number>) => {
                                    let val = typeof e.target.value === "string" ? parseInt(e.target.value) : e.target.value
                                    setEditEquipment({
                                        ...editEquipment,
                                        equgrp_id: val === 0 ? undefined : val
                                    })
                                }}
                                label="機材グループ"
                                fullWidth
                                size="small"
                            >
                                <MenuItem value={0}>設定なし</MenuItem>
                                {equipmentGroupList.map(value => (
                                    <MenuItem value={value.equgrp_id} key={value.equgrp_id}>{value.equgrp_name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="body2">予約可能スタジオ</Typography>
                        <Stack
                            direction="row"
                            flexWrap="wrap"
                        >
                            {storeObjectState.studioList?.map(studioData => (
                                <React.Fragment key={studioData.std_id}>
                                    {studioData.std_id && studioData.std_name &&
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={(editEquipment?.available_studio_ids) ? editEquipment.available_studio_ids.includes(studioData.std_id) : false}
                                                    onChange={(e) => {
                                                        let available_studio_ids = editEquipment.available_studio_ids ? editEquipment.available_studio_ids : []
                                                        if (studioData.std_id) {
                                                            if (e.target.checked) {
                                                                available_studio_ids.push(studioData.std_id)
                                                            } else {
                                                                available_studio_ids = available_studio_ids.filter(value => value !== studioData.std_id)
                                                            }
                                                            setEditEquipment({
                                                                ...editEquipment,
                                                                available_studio_ids: available_studio_ids,
                                                            })
                                                        }
                                                    }}
                                                />
                                            }
                                            label={studioData.std_name}
                                        />
                                    }
                                </React.Fragment>

                            ))}
                        </Stack>
                    </Grid>

                    <Grid item
                        sx={{
                            mx: 0,
                            mt: 2,
                            marginLeft: "auto",
                            textAlign: "center",
                        }}
                        xs={12}
                    >
                        <Stack
                            spacing={1}
                            direction="row"
                            justifyContent="right"
                        >
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    doSave(editEquipment)
                                }}
                                disabled={disableSubmit}
                            >保存</Button>
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    onClose(false)
                                }}
                            >キャンセル</Button>
                        </Stack>
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    );
}

export default EquipmentEditDialog